/* La Sura cake builder (form= inputs): sort, dietary filter, multi-select, allergen
   line, missing choices. Allergens are load-bearing: see flow.allergen_disclosure. */
class LasuraCakeBuilder extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.allergenLine = this.querySelector('[data-builder-allergens]');
    this.missingLine = this.querySelector('[data-builder-missing]');
    this.strings = this.readJSON('[data-builder-strings]', {});
    const base = this.readJSON('[data-builder-base-allergens]', {});
    this.base = { c: base.c || [], m: base.m || [] };
    this.invalidAt = 0;

    this.sortOptions();
    this.setupMultiGroups();

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('input', (e) => this.onInput(e));
    this.addEventListener('invalid', (e) => this.onInvalid(e), true);

    const message = this.querySelector('[data-builder-message]');
    if (message) this.updateCount(message);

    this.refresh();
  }

  readJSON(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector).textContent) ?? fallback;
    } catch {
      return fallback;
    }
  }

  fill(template, values) {
    return Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template || '');
  }

  sortOptions() {
    this.querySelectorAll('.lasura-group__opts').forEach((wrap) => {
      const opts = [...wrap.querySelectorAll('[data-builder-option]')];
      if (opts.length < 2) return;
      opts
        .sort((a, b) => Number(a.dataset.sort || 999) - Number(b.dataset.sort || 999))
        .forEach((opt) => wrap.appendChild(opt));
    });
  }

  setupMultiGroups() {
    this.querySelectorAll('[data-multi]').forEach((group) => {
      group.querySelectorAll('input[type="checkbox"][name]').forEach((box) => box.removeAttribute('name'));
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.dataset.multiValue = '';
      hidden.setAttribute('form', this.dataset.formId);
      hidden.dataset.property = group.dataset.property || '';
      group.appendChild(hidden);
    });
  }

  selectedNeeds() {
    return [...this.querySelectorAll('[data-dietary-need]:checked')].map((box) => box.value);
  }

  onChange({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-dietary-none]')) {
      this.querySelectorAll('[data-dietary-need]').forEach((box) => (box.checked = false));
      target.checked = true;
    } else if (target.matches('[data-dietary-need]')) {
      /* A need replaces "Nothing to avoid"; unticking the last need leaves
         the question unanswered, never answered for them. */
      const none = this.querySelector('[data-dietary-none]');
      if (none && target.checked) none.checked = false;
    }

    const group = target.closest('[data-builder-group]');
    if (group) this.clearError(group);

    this.refresh(target.matches('[data-dietary-none], [data-dietary-need]'));
  }

  onInput({ target }) {
    if (target.matches?.('[data-builder-message]')) this.updateCount(target);
    if (target.matches?.('[data-builder-phone], [data-detail]')) this.clearError(target.closest('[data-builder-group]'));
  }

  refresh(dietaryChanged = false) {
    const cleared = this.applyDietaryFilter();
    if (dietaryChanged) this.showCleared(cleared);
    this.writeMultiValues();
    this.updateAllergens();
  }

  applyDietaryFilter() {
    const needs = this.selectedNeeds();
    const cleared = [];

    this.querySelectorAll('[data-builder-group]').forEach((group) => {
      const opts = [...group.querySelectorAll('[data-builder-option]')];
      if (!opts.length || group.hasAttribute('data-diet-skip')) return;
      let shown = 0;

      opts.forEach((opt) => {
        const input = opt.querySelector('input');
        const suitable = (opt.dataset.suitable || '').split('|').filter(Boolean);
        const ok = needs.length === 0 || (suitable.length > 0 && needs.every((need) => suitable.includes(need)));
        opt.hidden = !ok;
        if (ok) shown += 1;
        if (!ok && input && input.checked) {
          input.checked = false;
          cleared.push(input.value);
        }
        if (input && input.type === 'radio' && group.hasAttribute('data-required')) input.required = ok;
      });
      if (!shown && group.hasAttribute('data-required')) opts.forEach((opt) => (opt.querySelector('input').required = true));

      const note = group.querySelector('[data-builder-note]');
      if (!note) return;
      const nl = group.querySelector('[data-builder-none-link]');
      if (nl) nl.hidden = shown || !needs.length;
      if (needs.length === 0 || shown === opts.length) note.textContent = '';
      else if (shown === 0) note.textContent = this.strings.noneSuit || '';
      else note.textContent = this.fill(this.strings.showing, { shown, total: opts.length });
    });

    return cleared;
  }

  showCleared(cleared) {
    const note = this.querySelector('[data-builder-cleared]');
    if (!note) return;
    note.textContent = cleared.length ? this.fill(this.strings.cleared, { choices: this.listJoin(cleared) }) : '';
  }

  listJoin(items) {
    if (items.length < 2) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
  }

  writeMultiValues() {
    this.querySelectorAll('[data-multi]').forEach((group) => {
      const hidden = group.querySelector('[data-multi-value]');
      if (!hidden) return;
      const values = [...group.querySelectorAll('input[type="checkbox"]:checked')]
        .filter((box) => box.value && !box.closest('[hidden]'))
        .map((box) => box.value);
      const text = values.join(', ') || group.dataset.emptyValue || '';
      if (text) {
        hidden.name = `properties[${hidden.dataset.property}]`;
        hidden.value = text;
      } else {
        hidden.removeAttribute('name');
        hidden.value = '';
      }
    });
  }

  chosenOptions() {
    return [...this.querySelectorAll('[data-builder-option]')].filter((opt) => {
      const input = opt.querySelector('input');
      return input && input.checked && !opt.hidden;
    });
  }

  /* The whole cake: the base declaration plus the choices (Matt,
     2026-09-30). None declared: no line; the allergen block says to ask. */
  updateAllergens() {
    if (!this.allergenLine) return;
    const contains = new Set(this.base.c), may = new Set(this.base.m);
    let note; /* its note: always said */
    this.chosenOptions().forEach((opt) => {
      (opt.dataset.allergens || '').split('|').filter(Boolean).forEach((a) => contains.add(a));
      (opt.dataset.mayContain || '').split('|').filter(Boolean).forEach((a) => may.add(a));
      note ||= opt.dataset.allergensNote;
    });
    const mayOnly = [...may].filter((a) => !contains.has(a)).sort(), parts = [];
    if (contains.size) parts.push(`${this.strings.adds || ''} ${[...contains].sort().join(', ')}.`);
    if (mayOnly.length) parts.push(`${this.strings.mayAdd || ''} ${mayOnly.join(', ')}.`);
    this.allergenLine.hidden = (!this.base.c.length || !parts.length) && !note;
    this.allergenLine.textContent = (this.base.c.length || !note ? parts : []).concat(note || []).join(' ');
  }

  onInvalid(event) {
    const field = event.target;
    const group = field.closest('[data-builder-group]');
    if (!group) return;
    event.preventDefault();
    const now = performance.now();
    const first = now - this.invalidAt > 400;
    if (first) this.missing = [];
    this.invalidAt = now;
    /* A follow-up answer (data-missing) is named itself, not its group. */
    if (field.dataset.missing) field.setAttribute('aria-invalid', 'true');
    else this.setError(group, true);
    const name = field.dataset.missing || group.dataset.groupLabel;
    if (name && !this.missing.includes(name)) this.missing.push(name);
    this.renderMissing();
    if (!first) return;
    const target = field.closest('[hidden]') ? group.querySelector('[data-builder-option]:not([hidden]) input') : field;
    /* Step by step: the heading to its usual place. */
    const steps = this.closest('.lasura-steps-on')?.querySelector('lasura-cake-steps');
    if (steps?.bring) steps.bring();
    else group.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    if (target) target.focus({ preventScroll: true });
  }

  setError(group, on) {
    const error = group.querySelector('[data-builder-error]');
    if (error) error.hidden = !on;
    const text = group.querySelector('input[type="tel"], input[type="text"]');
    if (text && on) text.setAttribute('aria-invalid', 'true');
    else if (text) text.removeAttribute('aria-invalid');
  }

  clearError(group) {
    if (!group) return;
    this.setError(group, false);
    if (!this.missing) return;
    const own = [group.dataset.groupLabel, ...[...group.querySelectorAll('[data-missing]')].map((f) => f.dataset.missing)];
    this.missing = this.missing.filter((name) => !own.includes(name));
    this.renderMissing();
  }

  renderMissing() {
    if (!this.missingLine) return;
    this.missingLine.hidden = this.missing.length === 0;
    this.missingLine.textContent = this.fill(this.strings.missing, { groups: this.listJoin(this.missing) });
  }

  weight(text) {
    return [...text].reduce((n, ch) => n + (ch !== ch.toLowerCase() && ch === ch.toUpperCase() ? 2 : 1), 0);
  }

  updateCount(input) {
    const max = Number(input.getAttribute('maxlength')) || 30;
    let value = input.value;
    while (this.weight(value) > max) value = value.slice(0, -1);
    if (value !== input.value) input.value = value;
    const out = this.querySelector('[data-builder-message-count]');
    if (out) out.textContent = String(Math.max(0, max - this.weight(value)));
  }
}

if (!customElements.get('lasura-cake-builder')) {
  customElements.define('lasura-cake-builder', LasuraCakeBuilder);
}
